import axios from 'axios';
import { env } from '../config/env';

const API_URL = 'https://api.openai.com/v1/chat/completions';

export const summarizeTaskDescription = async (description: string): Promise<string> => {
  try {
    const response = await axios.post(
      API_URL,
      {
        model: 'gpt-3.5-turbo',
        messages: [
          {
            role: 'system',
            content: 'Você é um assistente especializado em analisar e resumir textos de tarefas. Mantenha os resumos concisos e focados no objetivo principal.'
          },
          {
            role: 'user',
            content: `Por favor, resuma o objetivo principal desta tarefa em um parágrafo curto e claro:\n\n${description}`
          }
        ],
        temperature: 0.7,
        max_tokens: 200
      },
      {
        headers: {
          'Authorization': `Bearer ${env.chatGptApiKey}`,
          'Content-Type': 'application/json'
        }
      }
    );

    const content = response.data?.choices?.[0]?.message?.content?.trim();
    if (!content) {
      throw new Error('Conteúdo da resposta não encontrado');
    }

    return content;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401) {
        throw new Error('Chave de API inválida ou expirada');
      }
      if (error.response?.status === 429) {
        throw new Error('Limite de requisições excedido');
      }
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error.message || 'Erro na API do OpenAI');
      }
    }
    throw new Error('Não foi possível gerar o resumo da tarefa');
  }
};

export const extractTimelineEvents = async (content: string): Promise<Array<{ date: string; event: string }>> => {
  try {
    const response = await axios.post(
      API_URL,
      {
        model: 'gpt-3.5-turbo',
        messages: [
          {
            role: 'system',
            content: `Você é um assistente especializado em analisar documentos e extrair eventos em ordem cronológica.
            
            IMPORTANTE:
            - Extraia APENAS eventos que já aconteceram
            - Ignore eventos futuros ou planejados
            - Mantenha a ordem cronológica
            - Padronize as datas no formato DD/MM/YYYY
            - Se uma data estiver em formato diferente, converta para DD/MM/YYYY
            - Se uma data estiver incompleta, tente inferir do contexto
            - Se não conseguir determinar a data exata, use o formato mais próximo possível
            
            Retorne apenas um JSON válido com a seguinte estrutura, sem texto adicional:
            {
              "events": [
                {
                  "date": "DD/MM/YYYY",
                  "event": "Descrição do evento"
                }
              ]
            }`
          },
          {
            role: 'user',
            content: `Extraia os eventos em ordem cronológica do seguinte texto e retorne apenas o JSON:\n\n${content}`
          }
        ],
        temperature: 0.3,
        max_tokens: 2000,
        response_format: { type: "json_object" }
      },
      {
        headers: {
          'Authorization': `Bearer ${env.chatGptApiKey}`,
          'Content-Type': 'application/json'
        }
      }
    );

    const responseContent = response.data?.choices?.[0]?.message?.content;
    if (!responseContent) {
      throw new Error('Conteúdo da resposta não encontrado');
    }

    try {
      const parsedContent = JSON.parse(responseContent);
      if (!parsedContent.events || !Array.isArray(parsedContent.events)) {
        throw new Error('Estrutura de eventos inválida');
      }

      return parsedContent.events.map(event => ({
        date: event.date || 'Data não especificada',
        event: event.event || 'Evento não especificado'
      }));
    } catch (parseError) {
      throw new Error('Formato de resposta inválido');
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401) {
        throw new Error('Chave de API inválida ou expirada');
      }
      if (error.response?.status === 429) {
        throw new Error('Limite de requisições excedido');
      }
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error.message || 'Erro na API do OpenAI');
      }
    }
    throw new Error('Não foi possível extrair a linha do tempo');
  }
};

export const analyzeResults = async (content: string): Promise<string[]> => {
  try {
    const response = await axios.post(
      API_URL,
      {
        model: 'gpt-3.5-turbo',
        messages: [
          {
            role: 'system',
            content: `Você é um assistente especializado em analisar documentos e identificar resultados concretos alcançados.
            
            IMPORTANTE:
            - Extraia APENAS os resultados finais e conquistas efetivamente alcançadas
            - NÃO inclua ações planejadas, em andamento ou futuras
            - NÃO inclua processos ou etapas intermediárias
            - Foque em resultados mensuráveis e tangíveis
            - Ignore qualquer menção a planos, intenções ou ações que serão realizadas
            
            Retorne apenas um JSON válido com a seguinte estrutura, sem texto adicional:
            {
              "results": [
                "Resultado concreto alcançado 1",
                "Resultado concreto alcançado 2"
              ]
            }`
          },
          {
            role: 'user',
            content: `Analise o texto a seguir e extraia APENAS os resultados concretos já alcançados, retornando apenas o JSON:\n\n${content}`
          }
        ],
        temperature: 0.3,
        max_tokens: 2000,
        response_format: { type: "json_object" }
      },
      {
        headers: {
          'Authorization': `Bearer ${env.chatGptApiKey}`,
          'Content-Type': 'application/json'
        }
      }
    );

    const responseContent = response.data?.choices?.[0]?.message?.content;
    if (!responseContent) {
      throw new Error('Conteúdo da resposta não encontrado');
    }

    try {
      const parsedContent = JSON.parse(responseContent);
      if (!parsedContent.results || !Array.isArray(parsedContent.results)) {
        throw new Error('Estrutura de resultados inválida');
      }

      return parsedContent.results;
    } catch (parseError) {
      throw new Error('Formato de resposta inválido');
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401) {
        throw new Error('Chave de API inválida ou expirada');
      }
      if (error.response?.status === 429) {
        throw new Error('Limite de requisições excedido');
      }
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error.message || 'Erro na API do OpenAI');
      }
    }
    throw new Error('Não foi possível analisar os resultados');
  }
};

export const translateToEnglish = async (text: string): Promise<string> => {
  try {
    const response = await axios.post(
      API_URL,
      {
        model: 'gpt-3.5-turbo',
        messages: [
          {
            role: 'system',
            content: 'You are a professional translator specialized in technical and business content. Translate the text from Portuguese to English maintaining the professional tone and technical accuracy.'
          },
          {
            role: 'user',
            content: `Translate the following text to English:\n\n${text}`
          }
        ],
        temperature: 0.3,
        max_tokens: 2000
      },
      {
        headers: {
          'Authorization': `Bearer ${env.chatGptApiKey}`,
          'Content-Type': 'application/json'
        }
      }
    );

    const content = response.data?.choices?.[0]?.message?.content?.trim();
    if (!content) {
      throw new Error('Translation content not found');
    }

    return content;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401) {
        throw new Error('Invalid or expired API key');
      }
      if (error.response?.status === 429) {
        throw new Error('Rate limit exceeded');
      }
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error.message || 'OpenAI API error');
      }
    }
    throw new Error('Could not translate the content');
  }
};

export const generateCategory = async (vertical: string | null, summary: string): Promise<string> => {
  try {
    const response = await axios.post(
      API_URL,
      {
        model: 'gpt-3.5-turbo',
        messages: [
          {
            role: 'system',
            content: `Você é um especialista em categorizar ações e tarefas técnicas.
            
            IMPORTANTE:
            - Analise o resumo e a vertical fornecidos
            - Gere uma categoria concisa (1-3 palavras)
            - Use termos técnicos apropriados
            - Mantenha consistência com a vertical quando possível
            - Evite categorias genéricas demais
            
            Retorne apenas a categoria, sem explicações ou texto adicional.`
          },
          {
            role: 'user',
            content: `Vertical: ${vertical || 'Não especificada'}
            
            Resumo da ação:
            ${summary}
            
            Gere uma categoria apropriada:`
          }
        ],
        temperature: 0.3,
        max_tokens: 50
      },
      {
        headers: {
          'Authorization': `Bearer ${env.chatGptApiKey}`,
          'Content-Type': 'application/json'
        }
      }
    );

    const category = response.data?.choices?.[0]?.message?.content?.trim();
    if (!category) {
      throw new Error('Categoria não gerada');
    }

    return category;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401) {
        throw new Error('Chave de API inválida ou expirada');
      }
      if (error.response?.status === 429) {
        throw new Error('Limite de requisições excedido');
      }
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error.message || 'Erro na API do OpenAI');
      }
    }
    throw new Error('Não foi possível gerar a categoria');
  }
};